/** Shared types and helpers for hybrid deposit step 1 + confirm pages. */

import {
	HYBRID_DEPOSIT_METHOD_BANK_TRANSFER,
	HYBRID_DEPOSIT_METHOD_FPAY,
	HYBRID_DEPOSIT_METHOD_FPAY_DUITNOW,
	HYBRID_DEPOSIT_METHOD_FPAY_EWALLET,
	HYBRID_DEPOSIT_METHOD_JGO,
	HYBRID_DEPOSIT_METHOD_JGO_DUITNOW,
	HYBRID_DEPOSIT_METHOD_JGO_EWALLET,
	HYBRID_DEPOSIT_METHOD_ONEMPAY,
	HYBRID_DEPOSIT_METHOD_RAPIDPAYS_DUITNOW,
	HYBRID_DEPOSIT_METHOD_RAPIDPAYS_ONLINE_BANKING,
	HYBRID_DEPOSIT_METHOD_SEAPAY_DUITNOW,
	HYBRID_DEPOSIT_METHOD_SEAPAY_EWALLET,
	HYBRID_DEPOSIT_METHOD_SEAPAY_ONLINE_BANKING,
	type HybridDepositChannel,
	type HybridReceiverBankAccountRow,
} from "../../../../services/hybridPlayer.service";

export const QUICK_DEPOSIT_AMOUNTS = [50, 100, 200, 500, 1000] as const;

/** Legacy id `"fpay"` is accepted in router state and normalized to online banking. */
export type DepositChannelId =
	| "fpay_online_banking"
	| "fpay_ewallet"
	| "fpay_duitnow_qr"
	| "onempay"
	| "rapidpays_online_banking"
	| "rapidpays_duitnow_qr"
	| "jgo"
	| "jgo_fpx"
	| "jgo_ewallet"
	| "jgo_duitnow_qr"
	| "seapay_online_banking"
	| "seapay_ewallet"
	| "seapay_duitnow_qr"
	| "bank_transfer"
	| "reload_pin";

/**
 * Channels submitted through `hybridPlayerSubmitDepositGateway` (one
 * endpoint; user_api picks the gateway's adapter by its code).
 */
const GENERIC_GATEWAY_DEPOSIT_CHANNEL_IDS: ReadonlySet<DepositChannelId> =
	new Set<DepositChannelId>([
		"rapidpays_online_banking",
		"rapidpays_duitnow_qr",
		"jgo",
		"jgo_fpx",
		"jgo_ewallet",
		"jgo_duitnow_qr",
		"seapay_online_banking",
		"seapay_ewallet",
		"seapay_duitnow_qr",
	]);

export type DepositChannelConfig = {
	id: DepositChannelId;
	labelKey: string;
	iconType: "fontawesome" | "image";
	/** Font Awesome class(es), e.g. `fa fa-university` */
	faClass?: string;
	/** Path under `public/`, e.g. `/icons/fpay.png` */
	imageSrc?: string;
	/**
	 * For FPay options: `method_type_id` sent to `hybridPlayerSubmitDepositFPay`.
	 * For 1MPAY: same id (34) is sent to `hybridPlayerSubmitDepositOnempay` (see confirm page).
	 */
	fpayMethodTypeId?: number;
};

/** Default FPay `method_type_id`; same as `HYBRID_DEPOSIT_METHOD_FPAY`. */
export const DEFAULT_FPAY_METHOD_TYPE_ID = HYBRID_DEPOSIT_METHOD_FPAY;

// Channels that don't need a b2c_payment_gateway row — bank transfer's range
// comes from the receiver account's own limit group, and reload pin isn't a
// payment gateway at all.
export const STATIC_DEPOSIT_CHANNELS: DepositChannelConfig[] = [
	{
		id: "bank_transfer",
		labelKey: "Bank Transfer",
		iconType: "fontawesome",
		faClass: "fa fa-university",
	},
	{
		id: "reload_pin",
		labelKey: "Reload Pin",
		iconType: "fontawesome",
		faClass: "fa fa-ticket",
	},
];

// Display metadata (id/label/icon) per gateway code — the one thing that
// genuinely still lives in the frontend, since a brand new gateway code
// needs its own icon/label regardless. Whether a channel is actually
// AVAILABLE, and its min/max, is DB-driven (see buildDepositChannels).
// RAPIDPAYS / JGO / SEAPAY use generic Font Awesome icons until they get
// their own logos under public/icons.
const GATEWAY_CODE_DISPLAY: Record<
	string,
	Pick<
		DepositChannelConfig,
		"id" | "labelKey" | "iconType" | "faClass" | "imageSrc"
	>
> = {
	FPAY_ONLINE_BANKING: {
		id: "fpay_online_banking",
		labelKey: "Online Banking",
		iconType: "image",
		imageSrc: "/icons/fpay.png",
	},
	FPAY_EWALLET: {
		id: "fpay_ewallet",
		labelKey: "EWallet",
		iconType: "image",
		imageSrc: "/icons/fpay.png",
	},
	FPAY_DUITNOW_QR: {
		id: "fpay_duitnow_qr",
		labelKey: "DuitNow (QR)",
		iconType: "image",
		imageSrc: "/icons/fpay.png",
	},
	"1MPAY": {
		id: "onempay",
		labelKey: "1MPAY",
		iconType: "image",
		imageSrc: "/icons/1mpay.png",
	},
	RAPIDPAYS_ONLINE_BANKING: {
		id: "rapidpays_online_banking",
		labelKey: "RapidPays Online Banking",
		iconType: "fontawesome",
		faClass: "fa fa-university",
	},
	RAPIDPAYS_DUITNOW_QR: {
		id: "rapidpays_duitnow_qr",
		labelKey: "RapidPays DuitNow QR",
		iconType: "fontawesome",
		faClass: "fa fa-qrcode",
	},
	JGO: {
		id: "jgo",
		labelKey: "JGO Pay",
		iconType: "fontawesome",
		faClass: "fa fa-university",
	},
	JGO_FPX: {
		id: "jgo_fpx",
		labelKey: "JGO FPX",
		iconType: "fontawesome",
		faClass: "fa fa-university",
	},
	JGO_EWALLET: {
		id: "jgo_ewallet",
		labelKey: "JGO eWallet",
		iconType: "fontawesome",
		faClass: "fa-solid fa-wallet",
	},
	JGO_DUITNOW_QR: {
		id: "jgo_duitnow_qr",
		labelKey: "JGO DuitNow QR",
		iconType: "fontawesome",
		faClass: "fa fa-qrcode",
	},
	SEAPAY_ONLINE_BANKING: {
		id: "seapay_online_banking",
		labelKey: "SeaPay Online Banking",
		iconType: "fontawesome",
		faClass: "fa fa-university",
	},
	SEAPAY_EWALLET: {
		id: "seapay_ewallet",
		labelKey: "SeaPay eWallet",
		iconType: "fontawesome",
		faClass: "fa-solid fa-wallet",
	},
	SEAPAY_DUITNOW_QR: {
		id: "seapay_duitnow_qr",
		labelKey: "SeaPay DuitNow QR",
		iconType: "fontawesome",
		faClass: "fa fa-qrcode",
	},
};

// Every channel this frontend knows how to display, whether or not it is
// currently available. Used ONLY for label / method_type_id lookups (e.g. the
// confirm page) — never to decide what the picker offers. Availability comes
// from buildDepositChannels(hybridGetDepositChannels()).
const KNOWN_GATEWAY_CHANNELS: DepositChannelConfig[] = [
	{
		id: "fpay_online_banking",
		labelKey: "Online Banking",
		iconType: "image",
		imageSrc: "/icons/fpay.png",
		fpayMethodTypeId: HYBRID_DEPOSIT_METHOD_FPAY,
	},
	{
		id: "fpay_ewallet",
		labelKey: "EWallet",
		iconType: "image",
		imageSrc: "/icons/fpay.png",
		fpayMethodTypeId: HYBRID_DEPOSIT_METHOD_FPAY_EWALLET,
	},
	{
		id: "fpay_duitnow_qr",
		labelKey: "DuitNow (QR)",
		iconType: "image",
		imageSrc: "/icons/fpay.png",
		fpayMethodTypeId: HYBRID_DEPOSIT_METHOD_FPAY_DUITNOW,
	},
	{
		id: "onempay",
		labelKey: "1MPAY",
		iconType: "image",
		imageSrc: "/icons/1mpay.png",
		fpayMethodTypeId: HYBRID_DEPOSIT_METHOD_ONEMPAY,
	},
	{
		id: "rapidpays_online_banking",
		labelKey: "RapidPays Online Banking",
		iconType: "fontawesome",
		faClass: "fa fa-university",
		fpayMethodTypeId: HYBRID_DEPOSIT_METHOD_RAPIDPAYS_ONLINE_BANKING,
	},
	{
		id: "rapidpays_duitnow_qr",
		labelKey: "RapidPays DuitNow QR",
		iconType: "fontawesome",
		faClass: "fa fa-qrcode",
		fpayMethodTypeId: HYBRID_DEPOSIT_METHOD_RAPIDPAYS_DUITNOW,
	},
	{
		id: "jgo",
		labelKey: "JGO Pay",
		iconType: "fontawesome",
		faClass: "fa fa-university",
		fpayMethodTypeId: HYBRID_DEPOSIT_METHOD_JGO,
	},
	{
		id: "jgo_fpx",
		labelKey: "JGO FPX",
		iconType: "fontawesome",
		faClass: "fa fa-university",
		fpayMethodTypeId: HYBRID_DEPOSIT_METHOD_JGO,
	},
	{
		id: "jgo_ewallet",
		labelKey: "JGO eWallet",
		iconType: "fontawesome",
		faClass: "fa-solid fa-wallet",
		fpayMethodTypeId: HYBRID_DEPOSIT_METHOD_JGO_EWALLET,
	},
	{
		id: "jgo_duitnow_qr",
		labelKey: "JGO DuitNow QR",
		iconType: "fontawesome",
		faClass: "fa fa-qrcode",
		fpayMethodTypeId: HYBRID_DEPOSIT_METHOD_JGO_DUITNOW,
	},
	{
		id: "seapay_online_banking",
		labelKey: "SeaPay Online Banking",
		iconType: "fontawesome",
		faClass: "fa fa-university",
		fpayMethodTypeId: HYBRID_DEPOSIT_METHOD_SEAPAY_ONLINE_BANKING,
	},
	{
		id: "seapay_ewallet",
		labelKey: "SeaPay eWallet",
		iconType: "fontawesome",
		faClass: "fa-solid fa-wallet",
		fpayMethodTypeId: HYBRID_DEPOSIT_METHOD_SEAPAY_EWALLET,
	},
	{
		id: "seapay_duitnow_qr",
		labelKey: "SeaPay DuitNow QR",
		iconType: "fontawesome",
		faClass: "fa fa-qrcode",
		fpayMethodTypeId: HYBRID_DEPOSIT_METHOD_SEAPAY_DUITNOW,
	},
];

const KNOWN_DEPOSIT_CHANNELS: DepositChannelConfig[] = [
	...KNOWN_GATEWAY_CHANNELS,
	...STATIC_DEPOSIT_CHANNELS,
];

/**
 * Builds the channels the picker may offer from `hybridGetDepositChannels` —
 * i.e. whichever b2c_payment_gateway rows are ACTIVE with an ACTIVE deposit
 * range config right now. Channels for gateway codes this frontend has no
 * display metadata for are skipped (add an icon/label to GATEWAY_CODE_DISPLAY
 * first). Static channels (bank transfer, reload pin) are always appended.
 * Pure: callers keep the result in React state so a change re-renders.
 */
export function buildDepositChannels(
	channels: HybridDepositChannel[]
): DepositChannelConfig[] {
	const gatewayChannels: DepositChannelConfig[] = channels
		.map((channel): DepositChannelConfig | null => {
			const display = GATEWAY_CODE_DISPLAY[channel.gateway_code];
			if (!display) {
				return null;
			}
			return {
				...display,
				fpayMethodTypeId: channel.method_type_id,
			};
		})
		.filter((ch): ch is DepositChannelConfig => ch != null);

	return [...gatewayChannels, ...STATIC_DEPOSIT_CHANNELS];
}

export function getDepositChannelLabelKey(channel: DepositChannelId): string {
	return (
		KNOWN_DEPOSIT_CHANNELS.find((ch) => ch.id === channel)?.labelKey ?? channel
	);
}

/** Maps deposit channel to backend `method_type_id` (null for reload pin). */
export function getDepositMethodTypeId(
	channel: DepositChannelId
): number | null {
	switch (channel) {
		case "fpay_online_banking":
			return HYBRID_DEPOSIT_METHOD_FPAY;
		case "fpay_ewallet":
			return HYBRID_DEPOSIT_METHOD_FPAY_EWALLET;
		case "fpay_duitnow_qr":
			return HYBRID_DEPOSIT_METHOD_FPAY_DUITNOW;
		case "onempay":
			return HYBRID_DEPOSIT_METHOD_ONEMPAY;
		case "rapidpays_online_banking":
			return HYBRID_DEPOSIT_METHOD_RAPIDPAYS_ONLINE_BANKING;
		case "rapidpays_duitnow_qr":
			return HYBRID_DEPOSIT_METHOD_RAPIDPAYS_DUITNOW;
		case "jgo":
		case "jgo_fpx":
			return HYBRID_DEPOSIT_METHOD_JGO;
		case "jgo_ewallet":
			return HYBRID_DEPOSIT_METHOD_JGO_EWALLET;
		case "jgo_duitnow_qr":
			return HYBRID_DEPOSIT_METHOD_JGO_DUITNOW;
		case "seapay_online_banking":
			return HYBRID_DEPOSIT_METHOD_SEAPAY_ONLINE_BANKING;
		case "seapay_ewallet":
			return HYBRID_DEPOSIT_METHOD_SEAPAY_EWALLET;
		case "seapay_duitnow_qr":
			return HYBRID_DEPOSIT_METHOD_SEAPAY_DUITNOW;
		case "bank_transfer":
			return HYBRID_DEPOSIT_METHOD_BANK_TRANSFER;
		default:
			return null;
	}
}

/** Label key for a bonus PG `method_type_id`, when it maps to a deposit channel. */
export function getDepositChannelLabelKeyForMethodTypeId(
	typeId: number
): string | null {
	const channel = KNOWN_DEPOSIT_CHANNELS.find(
		(ch) => getDepositMethodTypeId(ch.id) === typeId
	);
	return channel?.labelKey ?? null;
}

type BonusPgMethodRef = { type_id: number; type?: string | null };

/**
 * When a bonus is selected, only PG channels allowed by its config are shown
 * (reload pin is excluded). With no bonus, all channels are shown.
 */
export function getDepositChannelsForBonusSelection(
	selectedBonus: {
		pg_methods?: BonusPgMethodRef[];
		config?: { pg_methods?: BonusPgMethodRef[] } | null;
	} | null,
	availableChannels: DepositChannelConfig[]
): DepositChannelConfig[] {
	const methods = selectedBonus?.pg_methods?.length
		? selectedBonus.pg_methods
		: selectedBonus?.config?.pg_methods;
	if (!methods?.length) {
		return availableChannels;
	}
	const allowedIds = new Set(methods.map((method) => method.type_id));
	return availableChannels.filter((ch) => {
		if (ch.id === "reload_pin") {
			return false;
		}
		const methodId = getDepositMethodTypeId(ch.id);
		return methodId != null && allowedIds.has(methodId);
	});
}

/** Channels that use `hybridPlayerSubmitDepositFPay` instead of bank transfer + receipt. */
export function isFpayRedirectDepositChannel(
	channel: DepositChannelId
): boolean {
	return channel !== "bank_transfer" && channel !== "reload_pin";
}

export function isReloadPinDepositChannel(channel: DepositChannelId): boolean {
	return channel === "reload_pin";
}

/** RAPIDPAYS / JGO / SEAPAY channels (`hybridPlayerSubmitDepositGateway`). */
export function isGenericGatewayDepositChannel(
	channel: DepositChannelId
): boolean {
	return GENERIC_GATEWAY_DEPOSIT_CHANNEL_IDS.has(channel);
}

export function getFpaySubmitMethodTypeId(channel: DepositChannelId): number {
	const cfg = KNOWN_DEPOSIT_CHANNELS.find((c) => c.id === channel);
	return cfg?.fpayMethodTypeId ?? DEFAULT_FPAY_METHOD_TYPE_ID;
}

/** Normalize legacy `"fpay"` from older clients to current id. */
export function normalizeDepositChannelId(
	raw: unknown
): DepositChannelId | null {
	if (raw === "bank_transfer") return "bank_transfer";
	if (raw === "reload_pin") return "reload_pin";
	if (raw === "fpay") return "fpay_online_banking";
	if (
		raw === "fpay_online_banking" ||
		raw === "fpay_ewallet" ||
		raw === "fpay_duitnow_qr" ||
		raw === "onempay"
	) {
		return raw;
	}
	if (
		typeof raw === "string" &&
		GENERIC_GATEWAY_DEPOSIT_CHANNEL_IDS.has(raw as DepositChannelId)
	) {
		return raw as DepositChannelId;
	}
	return null;
}

export const RECEIPT_ACCEPT =
	"application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png";

export const MAX_RECEIPT_BYTES = 10 * 1024 * 1024;

export const ALLOWED_RECEIPT_MIMES = new Set([
	"application/pdf",
	"image/jpeg",
	"image/png",
]);

export function isAllowedReceiptFile(file: File): boolean {
	if (ALLOWED_RECEIPT_MIMES.has(file.type)) return true;
	const name = file.name.toLowerCase();
	return (
		name.endsWith(".pdf") ||
		name.endsWith(".jpg") ||
		name.endsWith(".jpeg") ||
		name.endsWith(".png")
	);
}

/** Passed from step 1 → `/deposit/confirm` via router location state. */
export type HybridDepositConfirmState = {
	depositChannel: DepositChannelId;
	amount: number;
	/** @deprecated Prefer bonusConfigId — claim tickets are no longer used. */
	playerBonusId?: number | null;
	bonusConfigId?: number | null;
};

/** Returns normalized state (e.g. legacy `"fpay"` → `"fpay_online_banking"`). */
export function parseHybridDepositConfirmState(
	x: unknown
): HybridDepositConfirmState | null {
	if (!x || typeof x !== "object") return null;
	const o = x as Record<string, unknown>;
	const channel = normalizeDepositChannelId(o.depositChannel);
	const amount = o.amount;
	const playerBonusIdRaw = o.playerBonusId;
	const playerBonusId =
		playerBonusIdRaw == null || playerBonusIdRaw === ""
			? null
			: Number(playerBonusIdRaw);
	const bonusConfigIdRaw = o.bonusConfigId;
	const bonusConfigId =
		bonusConfigIdRaw == null || bonusConfigIdRaw === ""
			? null
			: Number(bonusConfigIdRaw);
	if (
		channel == null ||
		typeof amount !== "number" ||
		!Number.isFinite(amount) ||
		amount <= 0
	) {
		return null;
	}
	if (
		playerBonusId != null &&
		(Number.isNaN(playerBonusId) || playerBonusId <= 0)
	) {
		return null;
	}
	if (
		bonusConfigId != null &&
		(Number.isNaN(bonusConfigId) || bonusConfigId <= 0)
	) {
		return null;
	}
	return {
		depositChannel: channel,
		amount,
		playerBonusId,
		bonusConfigId,
	};
}

export function isHybridDepositConfirmState(
	x: unknown
): x is HybridDepositConfirmState {
	return parseHybridDepositConfirmState(x) !== null;
}

/** Placeholder for deposit amount input from API `min` / `max`. */
export function formatDepositAmountPlaceholder(
	min: number | null | undefined,
	max: number | null | undefined,
	t: (key: string, options?: Record<string, unknown>) => string
): string {
	const hasMin = min != null && Number.isFinite(min);
	const hasMax = max != null && Number.isFinite(max);
	if (hasMin && hasMax) {
		return t("Min {{min}} - Max {{max}}", { min, max });
	}
	if (hasMin) {
		return t("Min {{min}}", { min });
	}
	if (hasMax) {
		return t("Max {{max}}", { max });
	}
	return t("Enter amount");
}

export function isAmountWithinMinMax(
	amount: number,
	min: number | null | undefined,
	max: number | null | undefined
): boolean {
	if (!Number.isFinite(amount) || amount <= 0) return false;
	if (min != null && Number.isFinite(min) && amount < min) return false;
	if (max != null && Number.isFinite(max) && amount > max) return false;
	return true;
}

/** Map `hybridGetReceiverBankAccount` row → display label for bank name. */
export function formatReceiverBankDisplayFromApi(
	row: HybridReceiverBankAccountRow
): string {
	const code = String(row.bank_code ?? "").trim();
	const name = String(row.bank_name ?? row.receiver_bank_name ?? "").trim();
	if (code && name) return `${code} - ${name}`;
	return name || code || "";
}

/** First row from `hybridGetReceiverBankAccount` `data` array. */
export function parseHybridReceiverBankAccountResponse(
	data: unknown
): HybridReceiverBankAccountRow | null {
	if (!Array.isArray(data) || data.length === 0) return null;
	const row = data[0] as Record<string, unknown>;
	const receiverBankName = String(row.receiver_bank_name ?? "").trim();
	const receiverBankAccNo = String(row.receiver_bank_acc_no ?? "").trim();
	const receiverBankAccName = String(row.receiver_bank_acc_name ?? "").trim();
	if (!receiverBankName || !receiverBankAccNo || !receiverBankAccName) {
		return null;
	}
	return {
		bank_account_setting_id: Number(row.bank_account_setting_id) || undefined,
		bank_setting_id: Number(row.bank_setting_id) || undefined,
		bank_name: String(row.bank_name ?? "").trim() || undefined,
		bank_code: String(row.bank_code ?? "").trim() || undefined,
		receiver_bank_name: receiverBankName,
		receiver_bank_acc_no: receiverBankAccNo,
		receiver_bank_acc_name: receiverBankAccName,
		ordering: Number(row.ordering) || undefined,
		bank_type: Number(row.bank_type) || undefined,
	};
}
